/**
 * «Монтажи» (спека §9.2 «Видео» п.3, §7; v2 §1.3–1.4, §1.6): список монтажей проекта — подпись состава
 * («Seedance 2.0 + Kling 3.0 Pro (кадры 1–2)» у смешанного), статус, длительность, цена клипов, переход,
 * титр, грейд, знак, музыка, авто/вручную, звезда «лучший»; «Пересобрать», плеер готового. У монтажа со
 * звездой — «Финал в 1080p» с оценкой (ПЛАТНО, ключ до успеха — `useFinalHd`; у моделей без 1080p
 * кнопка выключена с причиной `NO_HD`).
 *
 * «Собрать» → диалог: клипы одной модели (последний готовый у каждого кадра; у модели, отклоняющей лица,
 * кадры с лицом — клип запасной) или ручной выбор клипа по кадрам, переход, титр двумя строками
 * (по умолчанию выключен, строки пустые — сценарий титра не несёт), длительность титра, грейд «Milla Nova» (по умолчанию вкл.), знак —
 * PNG, музыка — mp3/m4a.
 *
 * Монтаж денег не стоит (ffmpeg у нас); такой же набор уже собран или собирается — сервер вернёт его
 * (`existing`). Нет ffmpeg — «Собрать» выключена с причиной.
 */

import clsx from "clsx";
import { Clapperboard, GripVertical, Music, RotateCcw, Sparkles, Star, Trash2, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Btn, Empty, ErrorLine, Hint, inputClass, Panel, selectClass } from "@/components/project/ui";
import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { ApiFailure } from "@/lib/api";
import {
  clampDuration,
  clampTrim,
  reclampItem,
  durationOf,
  OUTRO_MS_DEFAULT,
  OUTRO_MS_RANGE,
  OUTRO_TEXT_DEFAULT,
  OUTRO_TEXT_MAX,
  reorder,
  sumMs,
  toCutBody,
  type CutDraft,
  type CutDraftItem,
} from "@/lib/cutDraft";
import { mediaUrl, when } from "@/lib/media";
import { errorText, usd } from "@/lib/project";
import { resultPath } from "@/lib/results";
import {
  CUT_STATUS_LABEL,
  CUT_TRANSITION_LABEL,
  GRADE_MILLA,
  seconds,
  useAdoptCut,
  useCut,
  useFinalHd,
  useFinalHdEstimate,
  usePickCut,
  useDeleteCut,
  useRebuildCut,
  useUploadLogo,
  useUploadMusic,
  useVideoOptions,
  type Cut,
  type CutBody,
  type CutTransition,
  type TimelineShot,
  type VideoModel,
} from "@/lib/video";
import { titleSummary } from "@/lib/videoCompare";
import { cuttableModels, doneClips, plural } from "@/lib/videoRun";

import { Sec } from "./bits";
import { useModelLabels } from "./clips";

const CUT_PILL: Record<Cut["status"], string> = {
  queued: "bg-warn-bg text-warn",
  running: "bg-accent-dim text-accent-text",
  done: "bg-ok-bg text-ok",
  failed: "bg-err-bg text-err",
};

export function CutsPanel({
  pid,
  timeline,
  cuts,
}: {
  pid: number;
  timeline: TimelineShot[];
  cuts: Cut[];
}) {
  const options = useVideoOptions();
  const rebuild = useRebuildCut(pid);
  const remove = useDeleteCut(pid);
  const pick = usePickCut(pid);
  const adopt = useAdoptCut(pid);
  const [dialog, setDialog] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const ffmpeg = options.data?.ffmpeg;
  const noFfmpeg = ffmpeg !== undefined && !ffmpeg.ok;

  return (
    <Panel
      title="Монтажи"
      aside={
        <Btn
          disabled={noFfmpeg}
          title={noFfmpeg ? `нет ffmpeg — монтаж недоступен${ffmpeg?.error ? `: ${ffmpeg.error}` : ""}` : undefined}
          onClick={() => setDialog(true)}
        >
          <Clapperboard size={13} />
          Собрать
        </Btn>
      }
      annot="монтажи ролика"
    >
      {noFfmpeg ? <Hint className="mb-2">Нет ffmpeg — монтаж недоступен, клипы работают.</Hint> : null}
      {cuts.length === 0 ? (
        <Empty>Монтажей нет.</Empty>
      ) : (
        <ul className="grid gap-2">
          {cuts.map((c) => {
            const rebuilding = rebuild.isPending && rebuild.variables === c.id;
            const picking = pick.isPending && pick.variables === c.id;
            const adopting = adopt.isPending && adopt.variables === c.id;
            return (
              <li
                key={c.id}
                className="grid gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2.5"
                data-annot={`монтаж ${c.id}`}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
                  <button
                    type="button"
                    className={clsx("grid size-6 place-items-center rounded-full", c.pick === "picked" ? "text-accent" : "text-fg-faint hover:text-fg")}
                    title={c.pick === "picked" ? "лучший в проекте" : "отметить лучшим (прежняя отметка снимется)"}
                    aria-pressed={c.pick === "picked"}
                    disabled={picking || c.pick === "picked" || c.status !== "done"}
                    onClick={() => pick.mutate(c.id)}
                  >
                    <Star size={14} fill={c.pick === "picked" ? "currentColor" : "none"} />
                  </button>
                  <b className="font-medium text-fg" title={c.items.some((i) => i.routed_from) ? "смешанный: кадры с лицом — клипы запасной модели" : undefined}>
                    {c.label}
                  </b>
                  <span className={clsx("rounded-full px-2 py-0.5 text-[11.5px]", CUT_PILL[c.status])}>
                    {CUT_STATUS_LABEL[c.status]}
                  </span>
                  <Sec ms={c.duration_ms} />
                  <span title="сумма факта клипов">
                    {c.cost !== null ? (
                      <>
                        клипы <span className="tnum">{usd(c.cost)}</span>
                      </>
                    ) : (
                      <i className="text-fg-muted">факт не у всех клипов</i>
                    )}
                  </span>
                  <span>{CUT_TRANSITION_LABEL[c.transition]}</span>
                  <span className="max-w-[260px] truncate" title={c.title ?? undefined}>
                    {(() => {
                      const t = titleSummary(c.title);
                      return t ? `титр «${t.first}»${t.more ? ` +${t.more}` : ""} ${seconds(c.title_ms)}` : "без титра";
                    })()}
                  </span>
                  <span className={c.grade ? undefined : "text-fg-faint"}>{c.grade ? "грейд Milla Nova" : "без грейда"}</span>
                  {c.logo_path ? (
                    <a href={mediaUrl(c.logo_path)} target="_blank" rel="noreferrer" className="text-accent-text hover:underline">
                      знак
                    </a>
                  ) : null}
                  {c.music_path ? (
                    <a href={mediaUrl(c.music_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent-text hover:underline">
                      <Music size={12} /> музыка
                    </a>
                  ) : (
                    <span className="text-fg-faint">без звука</span>
                  )}
                  <span className="text-fg-faint">
                    {c.origin === "auto" ? "авто" : c.author_name} · {when(c.date_create)}
                  </span>
                  <span className="ml-auto flex gap-1.5">
                    {c.status === "done" && c.asset_path ? (
                      <Btn tone="ghost" onClick={() => setPlaying(playing === c.id ? null : c.id)}>
                        {playing === c.id ? "скрыть" : "смотреть"}
                      </Btn>
                    ) : null}
                    {c.status === "done" && c.asset_path ? (
                      c.variant_id !== null ? (
                        <Link
                          to={resultPath(c.variant_id)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center text-accent-text hover:underline"
                        >
                          уже в результатах ↗
                        </Link>
                      ) : (
                        <Btn disabled={adopting} title="Бесплатно: файл не копируется" onClick={() => adopt.mutate(c.id)}>
                          <Upload size={12} />
                          {adopting ? "добавляю…" : "В результаты"}
                        </Btn>
                      )
                    ) : null}
                    {c.status === "done" || c.status === "failed" ? (
                      <Btn disabled={rebuilding || noFfmpeg} onClick={() => rebuild.mutate(c.id)}>
                        <RotateCcw size={12} />
                        {rebuilding ? "ставлю…" : "Пересобрать"}
                      </Btn>
                    ) : null}
                    {/* Удалить ненужный монтаж (№361): бесплатно, файл остаётся в хранилище.
                        Взятый в «Результаты» и монтаж с оплаченным финалом сервер не отдаст. */}
                    {c.status === "done" || c.status === "failed" ? (
                      <Btn
                        tone="ghost"
                        // Гасим только СВОЮ строку: соседние монтажи удалять никто не мешает.
                        disabled={remove.isPending && remove.variables === c.id}
                        title="удалить монтаж из списка (файл остаётся, деньги не тратились)"
                        // Действие необратимое и стоит вплотную к «Пересобрать» — спрашиваем.
                        onClick={() => {
                          if (window.confirm(`Удалить монтаж №${c.id}? Его можно собрать заново — это бесплатно.`)) {
                            remove.mutate(c.id);
                          }
                        }}
                      >
                        <Trash2 size={12} />
                        {remove.isPending && remove.variables === c.id ? "удаляю…" : "Удалить"}
                      </Btn>
                    ) : null}
                  </span>
                </div>
                {c.error ? <div className="text-[12px] text-err">{c.error}</div> : null}
                {adopt.isError && adopt.variables === c.id ? <ErrorLine text={errorText(adopt.error)} /> : null}
                {c.pick === "picked" && c.status === "done" ? <FinalHd pid={pid} cut={c} /> : null}
                {playing === c.id && c.asset_path ? (
                  <video
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    poster={c.poster_path ? mediaUrl(c.poster_path) : undefined}
                    src={mediaUrl(c.asset_path)}
                    className="max-h-[60vh] w-auto justify-self-start rounded-[var(--radius-ui)] bg-black"
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <ErrorLine text={rebuild.isError ? errorText(rebuild.error) : ""} className="mt-2" />
      {rebuild.data?.existing ? (
        <Hint className="mt-1">
          Монтаж №{rebuild.data.cut.id}: с прошлой сборки ничего не изменилось — новый не заводим. Чтобы взять другой
          клип, соберите монтаж заново («Собрать»).
        </Hint>
      ) : null}
      <ErrorLine text={remove.isError ? errorText(remove.error) : ""} className="mt-2" />
      <ErrorLine text={pick.isError ? errorText(pick.error) : ""} className="mt-2" />

      {/* `wide`: лента кусков горизонтальная (№351) — в окне 460 px её было видно на два куска. */}
      <Dialog open={dialog} title="Собрать монтаж" onClose={() => setDialog(false)} wide>
        {dialog ? (
          <CutForm
            pid={pid}
            timeline={timeline}
            models={options.data?.models ?? []}
            onDone={() => setDialog(false)}
          />
        ) : null}
      </Dialog>
    </Panel>
  );
}

/**
 * «Финал в 1080p» монтажа со звездой: оценка (бесплатно) → кнопка с ценой. `NO_HD` — у моделей клипов
 * нет формата больше: кнопка выключена с причиной. После успешной постановки кнопка заперта: клипы в
 * «Клипах», авто-монтаж соберётся из них сам; повтор — только после перезагрузки страницы, осознанно.
 */
function FinalHd({ pid, cut }: { pid: number; cut: Cut }) {
  const estimate = useFinalHdEstimate(cut.id, true);
  const run = useFinalHd(pid);
  const est = estimate.data;
  const noHd = estimate.error instanceof ApiFailure && estimate.error.code === "NO_HD";
  const reason = estimate.isError ? errorText(estimate.error) : "";
  const sent = run.data?.cut_id === cut.id;
  const clips = est?.items.length ?? 0;
  const skipped = [...new Set((est?.skipped ?? []).map((x) => x.reason))];
  return (
    <div className="grid gap-1 border-t border-border pt-2 text-[12px]" data-annot={`финал 1080p монтажа ${cut.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Btn
          tone="primary"
          disabled={!est || clips === 0 || run.busy || sent}
          title={noHd ? reason : "ПЛАТНО: клипы кадров заново в 1080p на тех же моделях, оплачивает воркер"}
          onClick={() => {
            if (est && clips > 0 && !sent) run.run(cut.id);
          }}
        >
          <Sparkles size={13} />
          {run.busy
            ? "ставлю в очередь…"
            : est && clips > 0
              ? `Финал в 1080p: ${clips} ${plural(clips, "клип", "клипа", "клипов")} ≈ ${usd(est.total, 2)}`
              : "Финал в 1080p"}
        </Btn>
        {estimate.isPending ? <Hint>считаю оценку…</Hint> : null}
        {reason ? <span className={noHd ? "text-fg-muted" : "text-err"}>{reason}</span> : null}
      </div>
      {skipped.length ? <Hint>Остаются как есть: {skipped.join("; ")}.</Hint> : null}
      {(est?.warnings ?? []).map((w) => (
        <div key={w} className="text-warn">
          {w}
        </div>
      ))}
      {est?.balance_warning ? <div className="text-warn">{est.balance_warning}</div> : null}
      {sent && run.data ? (
        <Hint>
          В очереди: {run.data.items.filter((i) => !i.existing).length}
          {run.data.items.some((i) => i.existing) ? ` (уже были: ${run.data.items.filter((i) => i.existing).length})` : ""} ≈{" "}
          {usd(run.data.total, 2)} — статус в «Клипах»; монтаж соберётся из новых клипов сам.
        </Hint>
      ) : null}
      <ErrorLine text={run.error} />
    </div>
  );
}

function CutForm({
  pid,
  timeline,
  models: catalog,
  onDone,
}: {
  pid: number;
  timeline: TimelineShot[];
  models: VideoModel[];
  onDone: () => void;
}) {
  const label = useModelLabels();
  const cut = useCut(pid);
  const upload = useUploadMusic(pid);
  const uploadLogo = useUploadLogo(pid);
  // У модели, отклоняющей лица, кадры с лицом берут клип запасной (`videocut._clip_model`).
  const faceFallback = (code: string) => {
    const m = catalog.find((x) => x.code === code);
    return m && m.faces === "rejects" ? m.face_fallback : null;
  };
  const models = cuttableModels(
    timeline,
    catalog.map((m) => m.code),
    faceFallback,
  );
  const mixedNote = (code: string) => {
    const fb = faceFallback(code);
    return fb && timeline.some((s) => s.duration_ms && s.face !== "hidden") ? ` (кадры с лицом — ${label(fb)})` : "";
  };
  const shots = timeline.filter((s) => s.duration_ms);
  const byShot = useMemo(() => new Map(shots.map((s) => [s.shot_id, s])), [shots]);
  const missingShots = shots.filter((s) => doneClips(s).length === 0);
  // Ручной набор — это и есть «собрать монтаж» (№351: «могу только посмотреть»), поэтому он открыт
  // ВСЕГДА. Условие «если у всех кадров есть клип» делало хуже ровно на недоделанном проекте:
  // там `models` пуст, радиокнопка «клипы одной модели» недоступна, и форма становилась мёртвой
  // (найдено ревью 16.10). «Клипы одной модели» остаются быстрым путём рядом.
  const [mode, setMode] = useState<"model" | "manual">("manual");
  const [model, setModel] = useState(models[0] ?? "");
  // Ручной набор — черновик монтажа (§9.1 спеки «Видео-ролик из готового кадра»): порядок кусков,
  // выбор клипа и обрезка начала у каждого. Кадры без готового клипа в список не входят — их
  // показываем отдельным предупреждением (`missingShots`), как раньше блокируя готовность.
  const [items, setItems] = useState<CutDraftItem[]>(() =>
    shots.flatMap((s) => {
      const first = doneClips(s)[0];
      return first
        ? [
            {
              shot_id: s.shot_id,
              variant_id: first.variant_id,
              file_ms: first.duration_ms ?? 0,
              shot_duration_ms: s.duration_ms ?? 0,
              trim_start_ms: 0,
            },
          ]
        : [];
    }),
  );
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const onDragOver = (overIdx: number) => {
    if (dragIdx === null || dragIdx === overIdx) return;
    setItems((prev) => reorder(prev, dragIdx, overIdx));
    setDragIdx(overIdx);
  };
  const setItemVariant = (shotId: number, variantId: number, fileMs: number) =>
    setItems((prev) =>
      prev.map((it) =>
        it.shot_id === shotId
          ? reclampItem({
              ...it,
              variant_id: variantId,
              file_ms: fileMs,
              trim_start_ms: clampTrim(it.trim_start_ms, fileMs),
            })
          : it,
      ),
    );
  const setItemTrimSec = (shotId: number, sec: number) =>
    setItems((prev) =>
      prev.map((it) =>
        it.shot_id === shotId
          ? reclampItem({ ...it, trim_start_ms: clampTrim(Math.round(sec * 1000), it.file_ms) })
          : it,
      ),
    );
  // Длительность куска правится здесь же (№353: «должна быть возможность обрезать длительность
  // видео именно на вкладке монтаж»); пусто — снова по длительности кадра.
  const setItemDurSec = (shotId: number, sec: number | null) =>
    setItems((prev) =>
      prev.map((it) =>
        it.shot_id === shotId
          ? { ...it, duration_ms: sec === null ? null : clampDuration(Math.round(sec * 1000), it) }
          : it,
      ),
    );
  const move = (idx: number, to: number) => setItems((prev) => reorder(prev, idx, to));

  const [transition, setTransition] = useState<CutTransition>("shots");
  const [titleOn, setTitleOn] = useState(false);
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [titleMs, setTitleMs] = useState(3000);
  const [grade, setGrade] = useState(true);
  const [music, setMusic] = useState<{ asset_id: number; name: string } | null>(null);
  const [logo, setLogo] = useState<{ asset_id: number; name: string; path: string } | null>(null);
  // Слайд-концовка (§9.2) — параметр монтажа независимо от режима набора кусков.
  const [outroOn, setOutroOn] = useState(false);
  const [outroText, setOutroText] = useState(OUTRO_TEXT_DEFAULT);
  const [outroSec, setOutroSec] = useState(OUTRO_MS_DEFAULT / 1000);
  const outroMs = Math.round(outroSec * 1000);

  const ready =
    shots.length > 0 &&
    (mode === "model" ? model !== "" : missingShots.length === 0 && items.length === shots.length) &&
    !cut.busy &&
    !upload.isPending &&
    !uploadLogo.isPending;

  const submit = () => {
    if (!ready) return;
    const title = [line1, line2].map((x) => x.trim()).filter(Boolean).join("\n");
    const draft: CutDraft = { items, outro: { on: outroOn, ms: outroMs, text: outroText.trim() || OUTRO_TEXT_DEFAULT } };
    const { items: draftItems, ...outroFields } = toCutBody(draft);
    const body: CutBody = {
      transition,
      title: titleOn ? title : "",
      title_ms: titleMs,
      grade: grade ? GRADE_MILLA : null,
      ...(mode === "model" ? { model } : { items: draftItems }),
      ...outroFields,
      ...(music ? { music_asset_id: music.asset_id } : {}),
      ...(logo ? { logo_asset_id: logo.asset_id } : {}),
    };
    cut.run(body);
  };

  return (
    <>
      <div className="grid gap-3 p-4 text-[12.5px]">
        <div className="flex gap-4">
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" checked={mode === "model"} disabled={models.length === 0} onChange={() => setMode("model")} />
            клипы одной модели
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} />
            выбрать по кадрам
          </label>
        </div>

        {mode === "model" ? (
          models.length === 0 ? (
            <Hint>Ни у одной модели нет готового клипа на каждом кадре — выберите клипы по кадрам.</Hint>
          ) : (
            <label className="grid gap-1">
              <span className="text-fg-faint">модель (последний готовый клип у каждого кадра, звезда — приоритет)</span>
              <select className={selectClass} value={model} onChange={(e) => setModel(e.target.value)}>
                {models.map((code) => (
                  <option key={code} value={code}>
                    {label(code)}
                    {mixedNote(code)}
                  </option>
                ))}
              </select>
            </label>
          )
        ) : (
          <div className="grid gap-1.5" data-annot="монтаж: ручной набор кусков">
            {shots.length === 0 ? <Hint>В проекте нет кадров с длительностью.</Hint> : null}
            <Hint>
              Куски идут слева направо — так они встанут в ролике. Порядок меняют стрелками «‹ ›» или
              перетаскиванием карточки; «с» — сколько секунд начала клипа пропустить, «длит.» — сколько секунд
              куска идёт в ролик (пусто — по длительности кадра).
            </Hint>
            {/* Лента кусков ГОРИЗОНТАЛЬНАЯ (задача Оли №351: «кадры выстроены в горизонтальный
                столбец, поставь их в горизонталь»): монтаж читается как дорожка, а не как список. */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {items.map((it, idx) => {
                const shot = byShot.get(it.shot_id);
                const options = shot ? doneClips(shot) : [];
                const poster = options.find((o) => o.variant_id === it.variant_id)?.poster_path ?? null;
                return (
                  <div
                    key={it.shot_id}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragEnter={() => onDragOver(idx)}
                    onDragEnd={() => setDragIdx(null)}
                    className={clsx(
                      "grid w-[168px] flex-none gap-1 rounded-[var(--radius-ui)] border bg-bg p-1.5 transition-opacity",
                      dragIdx === idx ? "border-accent opacity-60" : "border-border",
                    )}
                  >
                    <div className="flex items-center gap-1">
                      <GripVertical size={13} className="cursor-grab text-fg-faint active:cursor-grabbing" />
                      <span className="min-w-0 flex-1 truncate text-[12px]">
                        {shot?.order ?? idx + 1}. {shot?.title ?? `кадр ${it.shot_id}`}
                      </span>
                      <button
                        type="button"
                        title="левее"
                        disabled={idx === 0}
                        onClick={() => move(idx, idx - 1)}
                        className="px-1 text-fg-faint hover:text-fg disabled:opacity-30"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        title="правее"
                        disabled={idx === items.length - 1}
                        onClick={() => move(idx, idx + 1)}
                        className="px-1 text-fg-faint hover:text-fg disabled:opacity-30"
                      >
                        ›
                      </button>
                    </div>
                    <div className="grid h-[96px] place-items-center overflow-hidden rounded-[3px] border border-border bg-bg-subtle">
                      {poster ? (
                        <img src={mediaUrl(poster)} alt="" loading="lazy" className="h-full w-full object-contain" />
                      ) : (
                        <span className="text-[10px] text-fg-faint">нет постера</span>
                      )}
                    </div>
                    {options.length === 0 ? (
                      <span className="text-[11.5px] text-err">нет готового клипа</span>
                    ) : (
                      <select
                        className={clsx(selectClass, "w-full")}
                        value={it.variant_id}
                        onChange={(e) => {
                          const variantId = Number(e.target.value);
                          const c = options.find((o) => o.variant_id === variantId);
                          setItemVariant(it.shot_id, variantId, c?.duration_ms ?? it.file_ms);
                        }}
                      >
                        {options.map((c) => (
                          <option key={c.variant_id} value={c.variant_id}>
                            {label(c.model)} · {seconds(c.duration_ms)}
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="flex items-center gap-1 text-[11.5px]">
                      <label className="inline-flex items-center gap-1 tnum" title="обрезка начала куска, с">
                        с
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          value={(it.trim_start_ms / 1000).toFixed(1)}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isFinite(v)) setItemTrimSec(it.shot_id, v);
                          }}
                          className={clsx(inputClass, "w-12 px-1")}
                        />
                      </label>
                      <label className="inline-flex items-center gap-1 tnum" title="длительность куска в ролике, с">
                        длит.
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          placeholder={(durationOf({ ...it, duration_ms: null }) / 1000).toFixed(1)}
                          value={it.duration_ms == null ? "" : (it.duration_ms / 1000).toFixed(1)}
                          onChange={(e) => {
                            const raw = e.target.value.trim();
                            if (raw === "") return setItemDurSec(it.shot_id, null);
                            const v = Number(raw);
                            // «0» значит «как у кадра», а не «кусок в один выходной кадр»
                            if (Number.isFinite(v)) setItemDurSec(it.shot_id, v <= 0 ? null : v);
                          }}
                          className={clsx(inputClass, "w-12 px-1")}
                        />
                      </label>
                      <Sec ms={durationOf(it)} className="ml-auto text-fg-muted" />
                    </div>
                  </div>
                );
              })}
            </div>
            {missingShots.length > 0 ? (
              <Hint>
                Нет готового клипа у кадра {missingShots.map((s) => s.order).join(", ")} — сначала запустите клип на
                шаге «Видео».
              </Hint>
            ) : null}
            {items.length > 0 ? (
              <div className="text-[12px] text-fg-muted">
                итоговая длина ролика: <Sec ms={sumMs(items, outroOn ? outroMs : 0)} className="tnum text-fg" />
              </div>
            ) : null}
          </div>
        )}

        <label className="grid gap-1">
          <span className="text-fg-faint">переход</span>
          <select
            className={selectClass}
            value={transition}
            onChange={(e) => setTransition(e.target.value as CutTransition)}
          >
            {(Object.keys(CUT_TRANSITION_LABEL) as CutTransition[]).map((t) => (
              <option key={t} value={t}>
                {CUT_TRANSITION_LABEL[t]}
              </option>
            ))}
          </select>
        </label>

        <div className="grid gap-1">
          <label className="inline-flex items-center gap-1.5">
            <input type="checkbox" checked={titleOn} onChange={(e) => setTitleOn(e.target.checked)} />
            титр в конце ролика
          </label>
          {titleOn ? (
            <div className="flex flex-wrap gap-2">
              <div className="grid min-w-0 flex-1 gap-1">
                <input
                  className={clsx(inputClass, "min-w-0")}
                  value={line1}
                  maxLength={80}
                  placeholder="строка 1 — что это: СВАДЕБНОЕ ПЛАТЬЕ № 70"
                  aria-label="титр, строка 1"
                  onChange={(e) => setLine1(e.target.value)}
                />
                <input
                  className={clsx(inputClass, "min-w-0")}
                  value={line2}
                  maxLength={78}
                  placeholder="строка 2 — призыв: В ПРОКАТ"
                  aria-label="титр, строка 2"
                  onChange={(e) => setLine2(e.target.value)}
                />
              </div>
              <label className="inline-flex items-center gap-1 whitespace-nowrap">
                <input
                  type="number"
                  className={clsx(inputClass, "w-16")}
                  min={1}
                  max={5}
                  step={0.5}
                  value={titleMs / 1000}
                  onChange={(e) => {
                    const s = Number(e.target.value);
                    if (Number.isFinite(s)) setTitleMs(Math.round(Math.min(5, Math.max(1, s)) * 2) * 500);
                  }}
                />
                с
              </label>
            </div>
          ) : null}
        </div>

        <label className="inline-flex items-center gap-1.5" title="тёплый сдвиг, чуть меньше насыщенности и контраста, лёгкое зерно — модели сходятся по цвету">
          <input type="checkbox" checked={grade} onChange={(e) => setGrade(e.target.checked)} />
          грейд Milla Nova
        </label>

        <div className="grid gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg p-2.5" data-annot="монтаж: слайд-концовка">
          <label className="inline-flex items-center gap-1.5">
            <input type="checkbox" checked={outroOn} onChange={(e) => setOutroOn(e.target.checked)} />
            слайд-концовка {outroOn ? (logo ? "(знак, загруженный ниже)" : "(надпись)") : ""}
          </label>
          {outroOn ? (
            <div className="flex flex-wrap items-center gap-2">
              {logo ? (
                <Hint>Слайд — знак, надпись не нужна.</Hint>
              ) : (
                <input
                  value={outroText}
                  maxLength={OUTRO_TEXT_MAX}
                  placeholder={OUTRO_TEXT_DEFAULT}
                  aria-label="надпись слайда"
                  onChange={(e) => setOutroText(e.target.value)}
                  className={clsx(inputClass, "min-w-0 flex-1")}
                />
              )}
              <label className="inline-flex items-center gap-1 whitespace-nowrap">
                <input
                  type="number"
                  min={OUTRO_MS_RANGE[0] / 1000}
                  max={OUTRO_MS_RANGE[1] / 1000}
                  step={0.5}
                  value={outroSec}
                  aria-label="длительность слайда, с"
                  onChange={(e) => {
                    const s = Number(e.target.value);
                    if (Number.isFinite(s)) {
                      setOutroSec(Math.min(OUTRO_MS_RANGE[1] / 1000, Math.max(OUTRO_MS_RANGE[0] / 1000, s)));
                    }
                  }}
                  className={clsx(inputClass, "w-16")}
                />
                с
              </label>
            </div>
          ) : null}
        </div>

        <div className="grid gap-1">
          <span className="text-fg-faint">знак (PNG с прозрачностью до 2 МБ; в последние 1,5 с над титром)</span>
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={clsx(
                "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg px-3 hover:bg-bg-hover",
                uploadLogo.isPending && "opacity-50",
              )}
            >
              <Upload size={13} />
              {uploadLogo.isPending ? "загружаю…" : logo ? "заменить" : "загрузить"}
              <input
                type="file"
                accept=".png,image/png"
                className="hidden"
                disabled={uploadLogo.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file)
                    uploadLogo.mutate(file, {
                      onSuccess: (got) => setLogo({ asset_id: got.asset_id, name: file.name, path: got.path }),
                    });
                }}
              />
            </label>
            {logo ? (
              <>
                <img src={mediaUrl(logo.path)} alt="" className="h-6 max-w-[120px] rounded-[3px] bg-black/60 object-contain p-0.5" />
                <span className="truncate">{logo.name}</span>
                <Btn tone="ghost" onClick={() => setLogo(null)}>
                  убрать
                </Btn>
              </>
            ) : null}
          </div>
          <ErrorLine text={uploadLogo.isError ? errorText(uploadLogo.error) : ""} />
        </div>

        <div className="grid gap-1">
          <span className="text-fg-faint">музыка (mp3 или m4a; без файла — ролик без звука)</span>
          <div className="flex flex-wrap items-center gap-2">
            <label className={clsx("inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg px-3 hover:bg-bg-hover", upload.isPending && "opacity-50")}>
              <Upload size={13} />
              {upload.isPending ? "загружаю…" : music ? "заменить" : "загрузить"}
              <input
                type="file"
                accept=".mp3,.m4a,audio/mpeg,audio/mp4"
                className="hidden"
                disabled={upload.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file)
                    upload.mutate(file, { onSuccess: (got) => setMusic({ asset_id: got.asset_id, name: file.name }) });
                }}
              />
            </label>
            {music ? (
              <>
                <span className="truncate">{music.name}</span>
                <Btn tone="ghost" onClick={() => setMusic(null)}>
                  убрать
                </Btn>
              </>
            ) : null}
          </div>
          <ErrorLine text={upload.isError ? errorText(upload.error) : ""} />
        </div>

        <ErrorLine text={cut.error} />
        {cut.data ? (
          <Hint>
            {cut.data.existing ? "Такой монтаж уже есть — он в списке." : "Монтаж в очереди — появится в списке."}
          </Hint>
        ) : null}
      </div>
      <DialogFooter>
        <Btn tone="ghost" onClick={onDone}>
          {cut.data ? "Закрыть" : "Отмена"}
        </Btn>
        <Btn tone="primary" disabled={!ready} onClick={submit}>
          {cut.busy ? "ставлю…" : "Собрать"}
        </Btn>
      </DialogFooter>
    </>
  );
}
